import ExcelJS from 'exceljs'
import fileDownload from 'js-file-download'
import { GridApi, IRowNode, ColDef, Column } from 'ag-grid-community'

interface ExportOptions {
  filename?: string
  sheetName?: string
  onlySelected?: boolean
  onlyVisibleColumns?: boolean
  exportAllRows?: boolean
  excludeColumns?: string[] // array of field names to exclude
}

/**
 * Exports rows from an ag-Grid instance to an Excel file, including all formatting.
 */
export async function exportRowsToExcel(
  gridApi: GridApi,
  options: ExportOptions = {},
): Promise<void> {
  const {
    filename = 'exported-data.xlsx',
    sheetName = 'Sheet1',
    onlySelected = false,
    onlyVisibleColumns = false,
    exportAllRows = false,
  } = options

  // Get columns in display order for v31
  const columnDefs = gridApi.getColumnDefs() || []
  const visibleColumns = columnDefs.map((col: any) => col.field).filter(Boolean)
  
  const columns = visibleColumns
    .filter(
      (colId: string) =>
        !(options.excludeColumns && options.excludeColumns.includes(colId)),
    )

  // Get column definitions for formatting
  const colDefs: ColDef[] = columnDefs.filter((col: any) => 
    columns.includes(col.field)
  )

  // Get header names
  const headers = colDefs.map(
    (colDef) => colDef.headerName || colDef.field || '',
  )

  // Get row data
  let rowNodes: IRowNode<any>[] = []
  if (onlySelected) {
    rowNodes = gridApi.getSelectedNodes()
  } else if (exportAllRows) {
    gridApi.forEachNode((node: IRowNode<any>) => rowNodes.push(node))
  } else {
    gridApi.forEachNodeAfterFilterAndSort((node: IRowNode<any>) =>
      rowNodes.push(node),
    )
  }

  // Create workbook and worksheet
  const workbook = new ExcelJS.Workbook()
  const worksheet = workbook.addWorksheet(sheetName)

  // Add header row
  worksheet.addRow(headers)

  // Add data rows
  rowNodes.forEach((node) => {
    const row: any[] = []
    colDefs.forEach((columnDef, idx) => {
      const colId = columns[idx]
      let value = node.data ? node.data[colId] : undefined

      // Apply valueFormatter if present
      if (columnDef?.valueFormatter) {
        try {
          value =
            typeof columnDef.valueFormatter === 'function'
              ? columnDef.valueFormatter({
                  value,
                  data: node.data,
                  node,
                  colDef: columnDef,
                  column: null as any, // v30 compatibility
                  api: gridApi,
                  columnApi: null as any, // v30+ doesn't use columnApi
                  context: (gridApi as any).context,
                })
              : value
        } catch {
          // fallback to value
        }
      }
      // Apply cellRenderer if present (and not a React component)
      if (
        columnDef.cellRenderer &&
        typeof columnDef.cellRenderer === 'function'
      ) {
        try {
          value = columnDef.cellRenderer({
            value,
            data: node.data,
            node,
            colDef: columnDef,
            column: null as any,
            api: gridApi,
            context: (gridApi as any).context,
          })
          // If the renderer returns a React element, fallback to value
          if (typeof value === 'object' && value !== null && value.props) {
            value = node.data[colId]
          }
        } catch {
          // fallback to value
        }
      }
      row.push(value)
    })
    worksheet.addRow(row)
  })

  // Auto width for columns
  worksheet.columns.forEach((column, i) => {
    if (!column) return // skip if column is undefined
    let maxLength = headers[i].length
    if (typeof column.eachCell === 'function') {
      column.eachCell({ includeEmpty: true }, (cell) => {
        const cellValue = cell.value ? cell.value.toString() : ''
        if (cellValue.length > maxLength) {
          maxLength = cellValue.length
        }
      })
    }
    column.width = maxLength + 2
  })

  // Generate and download Excel file using js-file-download
  const buffer = await workbook.xlsx.writeBuffer()
  fileDownload(buffer, filename)
}
